import { useEffect, useRef } from 'react';
import { formatUnreadCount, hasWindow, isDocumentHidden } from '@/lib/notifications/dom';

/**
 * Maintains a `(N) Notifi` style prefix on `document.title` while there are
 * unread inbound messages AND the tab is in the background.
 *
 * Restores the original title automatically when:
 *   - the count drops to zero,
 *   - the tab becomes visible,
 *   - the component unmounts.
 *
 * The hook records the title it was given at mount and treats *that* as the
 * source of truth. If another part of the app updates the title while we're
 * in "badged" mode we'll temporarily clobber it — that's acceptable for the
 * inbox page and reverts as soon as conditions change.
 */
export function useDocumentTitleBadge(unreadCount: number, baseTitle?: string): void {
  // Cache the original title once; never re-read it while the hook is alive,
  // otherwise we'd "remember" our own `(3) Notifi` and never revert.
  const originalTitleRef = useRef<string | null>(null);
  // Tracks whether the current `document.title` is something we set, so the
  // visibility listener knows whether it owns a revert.
  const isBadgedRef = useRef(false);

  useEffect(() => {
    if (!hasWindow()) return;
    if (originalTitleRef.current === null) {
      originalTitleRef.current = baseTitle ?? document.title;
    }

    const apply = () => {
      const original = originalTitleRef.current ?? 'Notifi';
      if (unreadCount > 0 && isDocumentHidden()) {
        const badge = formatUnreadCount(unreadCount);
        const next = `(${badge}) ${original}`;
        if (document.title !== next) document.title = next;
        isBadgedRef.current = true;
      } else if (isBadgedRef.current) {
        document.title = original;
        isBadgedRef.current = false;
      }
    };

    apply();

    const onVisibility = () => apply();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    window.addEventListener('blur', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
      window.removeEventListener('blur', onVisibility);
    };
  }, [unreadCount, baseTitle]);

  // Always restore the original title on unmount, even if the effect above
  // never got a chance to revert (e.g. user navigated away with the tab hidden).
  useEffect(() => {
    return () => {
      if (!hasWindow()) return;
      if (originalTitleRef.current !== null && isBadgedRef.current) {
        document.title = originalTitleRef.current;
        isBadgedRef.current = false;
      }
    };
  }, []);
}
